import { useCallback, useEffect, useMemo } from "react";
import { format, parse } from "date-fns";
import { useSelector } from "react-redux";
import { useActions } from "../../../hooks/useActions";
import { useQuotePopup, useRecaptchaAction } from "../../../hooks";
import { initializeQuotationFlow } from "../../../redux/home-insurance/home-insurance.actions";
import { DOCUMENT_TYPES, DOCUMENT_TYPES_LABELS } from "../../../constants/documents";
import { quoteSchema } from "../constants/schemas";
import { formControls } from "../constants/forms";
import { InitialQuotation } from "../../../models/initial-quote";
import useDocumentNumberValidation from "./useDocumentNumberValidation";
import { useReactHookFormRegister } from "../../../hooks/useReactHookFormRegister";
import useDocumentNumberClearValues from "./useDocumentNumberClearValues";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"

const useQuotationForm = () => {
    const { recaptchaToken, execute } = useRecaptchaAction();
    const { submitting, data, isCustomerDocumentIdInvalid } = useSelector(state => ({
        isCustomerDocumentIdInvalid: state.HomeInsurance.isCustomerDocumentIdInvalid,
        ...state.HomeInsurance.quotation.initialQuotationForm,
    }));
    const { opened } = useQuotePopup();
    const actions = useActions(
        {
            initializeQuotationFlow,
        },
        []
    );
    const rhForm = useForm({
        defaultValues: {
            ...data,
            documentType: data.documentType || DOCUMENT_TYPES.DNI,
            birthDate: data.birthDate
                ? format(new Date(data.birthDate), "dd/MM/yyyy")
                : "",
            [formControls.personalDataConsent.key]: false,
            [formControls.contactConsent.key]: false,
        },
        mode: "onChange",
        resolver: yupResolver(quoteSchema)
    });

    const { register, handleSubmit, setValue, watch } = rhForm;

    const getBirthDate = date =>
        date instanceof Date ? date : parse(date, "dd/MM/yyyy", new Date());

    const onSubmit = useCallback(
        values => {
            actions.initializeQuotationFlow(
                new InitialQuotation({
                    ...values,
                    recaptchaToken,
                    birthDate: values.birthDate ? getBirthDate(values.birthDate) : null,
                })
            );
        },
        [actions, recaptchaToken]
    );

    useEffect(() => {
        if (opened) execute();
    }, [opened, execute]);


    const documentNumber = watch(formControls.documentNumber.key)
    const documentType = watch(formControls.documentType.key)

    const {
        validating,
        exists: documentExists,
        handleDocumentNumberBlur,
    } = useDocumentNumberValidation(documentNumber, documentType);


    useEffect(() => {
        register(formControls.documentType.key)
        register(formControls.birthDate.key)
        register(formControls.whichEnsure.key)
        register(formControls.housingType.key)
        register(formControls.personalDataConsent.key)
        register(formControls.contactConsent.key)
        register("documentExists", false)
    });

    useEffect(() => {
        setValue("documentExists", documentExists, true);
    }, [documentExists, setValue]);

    useDocumentNumberClearValues(rhForm, validating);

    const documentTypesOptions = useMemo(() => {
        return Object.keys(DOCUMENT_TYPES_LABELS).map(type => ({
            label: DOCUMENT_TYPES_LABELS[type],
            value: type,
        }));
    }, []);

    return {
        rhForm,
        handleOnSubmit: handleSubmit(onSubmit),
        handleDocumentNumberBlur,
        documentTypesOptions,
        documentExists,
        validating,
        isCustomerDocumentIdInvalid,
        submitting,
    };
};

export default useQuotationForm;
